/**
 * nav.ro frontend entry point — bundle-uit de Vite la public/build/assets/js/app-<hash>.js
 *
 * Ordine de incarcare in browser:
 *   1) common.min.js (defer) → Resort framework + globale ($, device) — LEGACY
 *   2) Per-page *.min.js (defer) → folosesc Resort + Swiper legacy
 *   3) app bundle (type=module) → ruleaza dupa defer; coexista cu legacy
 *
 * Strategy de migrare:
 *   - Modulele core/* re-definesc Node.prototype.on / off / find / etc. cu
 *     implementare IDENTICA cu legacy. Idempotent — ultimul wins.
 *   - Cand totul testeaza OK, scoatem <script src="common.min.js"> din header.php.
 */

// Vendor libs din npm — DISPONIBILE prin import {} from 'swiper'/'nouislider'
// pentru codul nou TS. NU sunt bundle-uite global pentru ca legacy per-page
// scripts (home.js, etc.) folosesc /js/swiper.min.js Swiper 6 cu API diferit
// (`.swiper-container` vs `.swiper`). Cand migram per-page, importam aici.
// import './vendor/swiper.js';     // disponibil cand migram per-page
// import './vendor/nouislider.js'; // disponibil cand migram per-page

// Resort framework core — re-definesc Node.prototype.* identic cu legacy.
import './core/util.js';        // isset / isfn / trigger / each / window.each
import './core/selector.js';    // window.$
import './core/device.js';      // window.device
import './core/style.js';       // css / offset / data / removeData / html / text / val / getProp / dxGetProp
import './core/dom.js';         // find / findChildren / index / siblings / next / prev / parents / NodeList.filter
import './core/events.js';      // on / off / trigger + customEvents.swipe + window.on/off
import './core/anim.js';        // slideDown / slideUp / slideToggle + dxFadeIn / dxFadeOut
import './core/resort.js';      // Resort class + window.Resort

// Widgets — Node.prototype extensions per page.
import './widgets/navmenu.js';  // navMenu (testi-group, layer-3 pagination)
import './widgets/tooltip.js';  // Tooltip (data-tooltip)
import './widgets/popup.js';    // popupBox (.popup-box siblings)

// Form classes — FormValidator + Form (cu native-fix integrat).
import './form/validator.js';   // FormValidator + Node.prototype.FormValidator
import './form/form.js';        // Form + Node.prototype.Form

// CSS-uri pentru librarii — comentate cat timp legacy *.min.css inca sunt
// incarcate. Decomentate per pagina cand migram.
// import 'swiper/css';
// import 'swiper/css/navigation';
// import 'swiper/css/pagination';
// import 'nouislider/dist/nouislider.css';

// Altcha CAPTCHA web component — registers <altcha-widget>. PERF-3: imported
// DYNAMICALLY and only on pages that actually render the widget, so the ~14 KB
// bundle stays off the ~280 form-less pages. app.ts is a deferred module, so it
// runs after the DOM is parsed → the query sees the server-rendered widget.
//
// PERF-REFLOW-ALTCHA: the import is additionally deferred to the first idle
// slice after load. Importing at app.ts time upgraded the element MID-LOAD, and
// the widget's mount reads layout (getBoundingClientRect) before the browser's
// first layout ever ran — a forced synchronous layout of the whole page (the
// console's "[Violation] Forced reflow" — 350ms on /colocare under 4x CPU
// throttle). After first paint the layout is clean, so the same reads cost ~0.
// Visibility gating (IntersectionObserver) is NOT an option here: the widget is
// display:none by design (ALTCHA-INVISIBLE-1 in new.index.min.css) so it never
// intersects. auto="onload" semantics are preserved — the PoW still solves
// unprompted, just one idle slice later, still seconds before any human can
// fill a form. The 2.5s timeout bounds the wait on busy pages; no rIC support
// (Safari < 15.4) → plain timer past first paint.
//
// PERF-SCROLL-ALTCHA (2026-09-02): once upgraded, the widget attaches scroll /
// pointermove / touchmove listeners and measures rects while the page scrolls
// (85-160ms of JS + 16-33ms of forced layout per 10s of scrolling on /colocare,
// traced on prod). So the import — and with it the upgrade — now waits until the
// widget's FORM comes within ~600px of the viewport, or until the first
// interaction with that form, whichever is first. The widget itself is
// display:none (ALTCHA-INVISIBLE-1), so the observer watches the enclosing form:
// an invisible target never intersects. No IntersectionObserver → idle import.
//
// Upgrading the element also injects its stylesheet, which costs a whole-document
// style recalculation (~110ms / 5000 nodes on /colocare). Never pay that while
// the page is moving: once the form is near, wait until no scroll event has
// fired for 250ms, then import in an idle slice (bounded). A direct interaction
// with the form (focus / pointer) imports at once.
const altchaWidgets = document.querySelectorAll('altcha-widget');
if (altchaWidgets.length) {
    let imported = false;
    const importAltcha = () => { if (imported) return; imported = true; void import('altcha'); };
    let lastScroll = 0;
    window.addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
    const importWhenSettled = () => {
        if (imported) return;
        if (performance.now() - lastScroll < 250) { setTimeout(importWhenSettled, 250); return; }
        if (typeof requestIdleCallback === 'function') requestIdleCallback(importAltcha, { timeout: 1500 });
        else setTimeout(importAltcha, 50);
    };
    const anchors = Array.from(altchaWidgets, (w) => w.closest('form') ?? w.parentElement ?? w);
    if (typeof IntersectionObserver === 'function') {
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { io.disconnect(); importWhenSettled(); }
        }, { rootMargin: '600px 0px' });
        anchors.forEach((a) => {
            io.observe(a);
            a.addEventListener('focusin', importAltcha, { once: true, passive: true });
            a.addEventListener('pointerdown', importAltcha, { once: true, passive: true });
        });
    } else if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(importAltcha, { timeout: 2500 });
    } else {
        setTimeout(importAltcha, 300);
    }
}

// Bootstrap — `new Resort(rt => {...})` care lega init modules per-pagina.
// Inlocuieste callback-ul final din legacy common.min.js.
import { startResortBootstrap } from './init/bootstrap.js';
startResortBootstrap();

if (import.meta.env.DEV) {
    console.log('[nav] dev mode — Vite HMR active');
}
